import React, { useEffect, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import {
  Check,
  ClockHistory,
  Icon1CircleFill,
  Icon2CircleFill,
  Icon3CircleFill,
  InfoCircle,
  QuestionCircle,
  Stickies,
} from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import useGetUrlLocale from "../../../../hooks/useGetUrlLocale";
import useIsDomainMode from "../../../../hooks/useIsDomainMode";
import { whiteLabelJRStep } from "../../../../qraphql/globalVar/state";

interface IFirstStepProps {
  setStep: (step: number) => void;
  userDomain: string;
}

enum COPY_TYPE {
  NS1 = "ns1",
  NS2 = "ns2",
}

export default function SecondStep({ setStep, userDomain }: IFirstStepProps) {
  const { t } = useTranslation();
  const isDomainMode = useIsDomainMode();
  const urlLocale = useGetUrlLocale();
  const [copyState, setCopyState] = useState<{ ns1: boolean; ns2: boolean }>({
    ns1: false,
    ns2: false,
  });

  useEffect(() => {
    const currentStep = document.querySelector("#domainStep2");
    if (currentStep) {
      currentStep.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, []);

  const handleClickCopy = (type: COPY_TYPE) => {
    if (type === COPY_TYPE.NS1) setCopyState({ ...copyState, ns1: true });
    if (type === COPY_TYPE.NS2) setCopyState({ ...copyState, ns2: true });

    setTimeout(() => {
      setCopyState({ ns1: false, ns2: false });
    }, 1000);
  };

  return (
    <>
      <h4 className="fs-4 mb-2">
        <span className="fw-bold">{t("whiteLabel.Setup & Connect")}</span>{" "}
        {userDomain}
      </h4>
      <div className="text-info mb-4">
        {t(
          "whiteLabel.Use name servers as easiest way to connect your domain."
        )}
      </div>
      <div className="text-info rounded-2 bg-light-gray d-inline-block p-2 mb-4">
        <ClockHistory className="ms-1 me-2 mb-1" />
        <span className="me-1 fs-7">
          {t("whiteLabel.All changes can take to 72 hours.")}
        </span>
      </div>

      <div className="border border-sec-background rounded-2 p-4 d-flex gap-2 mb-3">
        <div>
          <Icon1CircleFill className="text-success" />
        </div>
        <div>
          <div className="fs-8 fw-bold mb-1">
            {t("whiteLabel.Login to Your Provider Account")}
          </div>
          <div className="text-info">
            {t("whiteLabel.Login to your account and go to domains page. Find")}{" "}
            <Link className="text-success" to="https://me-cluster.com/">
              me-cluster.com
            </Link>{" "}
            {t("whiteLabel.and go to it’s settings page.")}
          </div>
        </div>
      </div>

      <div className="border border-sec-background rounded-2 p-4 d-flex gap-2 mb-3">
        <div>
          <Icon2CircleFill className="text-success" />
        </div>
        <div>
          <div className="fs-8 fw-bold mb-3">
            {t("whiteLabel.Update Name Servers")}
          </div>
          {isDomainMode ? (
            <div className="text-info mb-3">
              {t("whiteLabel.WhiteLabelFunctionality")}
            </div>
          ) : (
            <div className="text-info mb-3">{t("whiteLabel.addNewCname")}</div>
          )}

          <Col md={6} className="mb-3">
            <div className="rounded bg-light-gray p-3 d-flex justify-content-between align-items-center">
              {isDomainMode ? (
                <div>ns1.me-team.org</div>
              ) : (
                <div>proxyd.me-team.org</div>
              )}
              <Button
                variant="link"
                onClick={() => {
                  handleClickCopy(COPY_TYPE.NS1);
                  navigator.clipboard.writeText(
                    isDomainMode ? "ns1.me-team.org" : "proxyd.me-team.org"
                  );
                }}
              >
                {copyState.ns1 ? (
                  <Check className="text-success fs-5" />
                ) : (
                  <Stickies className="text-success fs-5" />
                )}
              </Button>
            </div>
          </Col>
          {isDomainMode ? (
            <Col md={6}>
              <div className="rounded bg-light-gray p-3 d-flex justify-content-between align-items-center">
                <div>ns2.me-team.org</div>
                <Button
                  variant="link"
                  onClick={() => {
                    handleClickCopy(COPY_TYPE.NS2);
                    navigator.clipboard.writeText("ns2.me-team.org");
                  }}
                >
                  {copyState.ns2 ? (
                    <Check className="text-success fs-5" />
                  ) : (
                    <Stickies className="text-success fs-5" />
                  )}
                </Button>
              </div>
            </Col>
          ) : null}

          <div className="text-info rounded-2 bg-light-gray d-inline-block p-2 mt-3">
            <InfoCircle className="ms-1 me-2 mb-1" />
            <span className="me-1 fs-7">
              {t(
                "whiteLabel.If you use a proxy server, you need to disable it when setting up the domain."
              )}
            </span>
          </div>
        </div>
      </div>

      <div className="border border-sec-background rounded-2 p-4 mb-3">
        <div className="d-flex gap-2 mb-4">
          <div>
            <Icon3CircleFill className="text-success" />
          </div>
          <div>
            <div className="fs-8 fw-bold mb-1">
              {t("whiteLabel.Verify Connection")}
            </div>
            <div className="text-info me-lg-5 pe-lg-5">
              {t("whiteLabel.verifyInfo")}
            </div>
          </div>
        </div>
        <Row className="justify-content-center gx-4 gy-3">
          <Col md={4} className="order-1 order-md-0 ps-lg-5">
            <Button
              variant="outline-dark"
              className="w-100 h-100 fw-normal p-2"
              onClick={() => {
                setStep(1);
                whiteLabelJRStep(7);
              }}
            >
              {t("whiteLabel.Back")}
            </Button>
          </Col>
          <Col md={4} className="pe-lg-5">
            <Button
              variant="success"
              className="w-100 h-100 p-2"
              onClick={() => {
                setStep(3);
                whiteLabelJRStep(9);
              }}
            >
              {t("whiteLabel.Verify Connection")}
            </Button>
          </Col>
        </Row>
      </div>

      <div className="d-flex text-info align-items-center mb-5 fs-7">
        <QuestionCircle className="me-2" />
        {t("whiteLabel.Need help?")}
        <Link
          className="text-success ms-1"
          to={
            process.env.REACT_APP_TICKET_URL +
            urlLocale +
            "/organizer-support-chat"
          }
        >
          {t("whiteLabel.Contact Us")}
        </Link>
      </div>
    </>
  );
}
